import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Card, CardBody, CardHeader, Button,
  Row, Col, Container, Spinner, FormGroup, Label, Input,
  Table, Badge
} from 'reactstrap';
import { format, parseISO, parse } from 'date-fns';
import { fr } from 'date-fns/locale';
import { FaBarcode, FaCalendarAlt, FaDownload, FaUser, FaFileAlt, FaSearch, FaSyncAlt } from 'react-icons/fa';
import { convertNumberToWords, formatPrice } from '../../helpers/utility';
import exportSalesDetailsPdf from '../../helpers/exportSalesDetailsPdf';
import Select from "react-select";
import { supplierActions } from '../../sagas/supplierSlice';
import { purchaseActions } from '../../sagas/purchaseSlice';
import { productActions } from '../../sagas/productSlice';
import DocumentPagination from '../Common/DocumentPagination';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const AchatsStatistiques = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { company } = useSelector(state => state.auth);
  const unites = useSelector(status => status.products.unites);

  const now = new Date();
  const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const pad = (n) => n.toString().padStart(2, '0');
  const formatDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const formatMonth = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
  const getMonthRange = (monthValue) => {
    const [year, month] = monthValue.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    return {
      dateDebut: formatDate(firstDay),
      dateFin: formatDate(lastDay)
    };
  };
  const currentMonth = formatMonth(todayDate);
  const currentMonthRange = getMonthRange(currentMonth);

  useEffect(() => {
    dispatch(productActions.findAllUnites());
    dispatch(purchaseActions.clearSupplierReport());
  }, []);

  const parseDate = (dateString) => {
    if (!dateString) return new Date();
    if (dateString.includes('-')) {
      return parseISO(dateString);
    }
    return parse(dateString, 'dd/MM/yyyy', new Date());
  };

  // Supplier report state
  const { suppliers, loading: suppliersLoading } = useSelector(state => state.suppliers);
  const { supplierReport, supplierReportLoading, supplierReportError } = useSelector(state => state.purchases);
  const supplierReportRef = useRef(null);
  const [filters, setFilters] = useState({
    supplierId: null,
    month: currentMonth,
    dateDebut: currentMonthRange.dateDebut,
    dateFin: currentMonthRange.dateFin,
    query: ''
  });
  const [searchValue, setSearchValue] = useState('');
  const [pagination, setPagination] = useState({
    pageNumber: 0,
    pageSize: 25
  });

  const fournisseurOptions = suppliers?.map(sup => ({
    solde: sup.solde,
    totalDebit: sup.totalDebit,
    totalCredit: sup.totalCredit,
    value: sup.id,
    label: sup.typeFournisseur === 'PARTICULIER' ? `${sup.nom} ${sup.prenom}` : sup.entreprise,
  })) ?? [];

  const fournisseurOptionsWithAll = [
    { value: null, label: t('common.all', 'Tous') },
    ...fournisseurOptions
  ];
  const articlePurchases = useMemo(() => (supplierReport?.articlePurchases || []).map(row => ({
    ...row, unit: (unites || []).find(unit => unit.code === row.unit)?.libelle || row.unit,
  })), [supplierReport, unites]);
  const totalElements = articlePurchases.length;
  const currentPageSize = pagination.pageSize;
  const totalPages = Math.ceil(totalElements / currentPageSize);
  const currentPageNumber = Math.min(pagination.pageNumber, Math.max(0, totalPages - 1));
  const bonsReception = useMemo(() => articlePurchases.slice(currentPageNumber * currentPageSize, (currentPageNumber + 1) * currentPageSize), [articlePurchases, currentPageNumber, currentPageSize]);
  const pageQuantityTotal = bonsReception.reduce((sum, row) => sum + Number(row.quantity || 0), 0);
  const pageAmountTotal = bonsReception.reduce((sum, row) => sum + Number(row.lineTotal || 0), 0);
  const fullQuantityTotal = articlePurchases.reduce((sum, row) => sum + Number(row.quantity || 0), 0);
  const fullAmountTotal = articlePurchases.reduce((sum, row) => sum + Number(row.lineTotal || 0), 0);
  const pageStart = totalElements === 0 ? 0 : currentPageNumber * currentPageSize + 1;
  const pageEnd = Math.min((currentPageNumber + 1) * currentPageSize, totalElements);

  // Effect for fetching data
  useEffect(() => {
    if (suppliers.length === 0) {
      // Fetch suppliers for supplier report
      const fetchSuppliers = async () => {
        dispatch(supplierActions.search({
          query: ""
        }));
      };

      fetchSuppliers();
    }
  }, []);

  // Generate supplier report
  const generateSupplierReport = useCallback(() => {
    dispatch(purchaseActions.generateSupplierReport({ ...filters, page: 0, size: 25 }));
  }, [dispatch, filters]);

  useEffect(() => { generateSupplierReport(); }, [generateSupplierReport]);

  useEffect(() => {
    const debounceId = window.setTimeout(() => {
      setPagination(current => ({ ...current, pageNumber: 0 }));
      setFilters(current => ({
        ...current,
        query: searchValue.trim()
      }));
    }, 450);

    return () => window.clearTimeout(debounceId);
  }, [searchValue]);

  const updateFilters = (nextValues) => {
    setPagination(current => ({ ...current, pageNumber: 0 }));
    setFilters(current => ({ ...current, ...nextValues }));
  };

  const submitSearch = (value = searchValue) => {
    setPagination(current => ({ ...current, pageNumber: 0 }));
    setFilters(current => ({
      ...current,
      query: value.trim()
    }));
  };

  const handlePageChange = (pageNumber) => {
    setPagination(current => ({ ...current, pageNumber }));
  };

  const handlePageSizeChange = (event) => {
    setPagination({
      pageNumber: 0,
      pageSize: Number(event.target.value)
    });
  };

  const handleMonthChange = (monthValue) => {
    if (!monthValue) {
      updateFilters({ month: '' });
      return;
    }

    const monthRange = getMonthRange(monthValue);
    updateFilters({
      month: monthValue,
      dateDebut: monthRange.dateDebut,
      dateFin: monthRange.dateFin
    });
  };

  const exportSupplierReportToPDF = () => {
    if (!supplierReport || supplierReportLoading) return;
    exportSalesDetailsPdf({
      kind: 'purchases', report: supplierReport, company, filters,
      clientLabel: fournisseurOptionsWithAll.find(option => option.value === filters.supplierId)?.label,
      rows: bonsReception, allRows: articlePurchases,
      pageNumber: currentPageNumber, pageSize: currentPageSize,
    });
  };

  return (
    <Container fluid className="page-content achats-statistiques-page">
      <Card className="shadow-sm mb-4 border-0 achats-shell">
        <CardHeader className="bg-white border-0 pb-2">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <span className="achats-title-icon">
                  <FaFileAlt />
                </span>
                <h4 className="mb-0 fw-bold">Details des achats</h4>
              </div>
              <div className="text-muted small">
                Quantités achetées par article sur la période choisie.
              </div>
            </div>
            <div className="d-none">
              <FaFileAlt className="me-2 text-primary" />
              Détails des achats
            </div>
            <div className="d-flex gap-2">
              <Button
                color="primary"
                onClick={() => generateSupplierReport()}
                disabled={supplierReportLoading}
              >
                {supplierReportLoading ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Génération...
                  </>
                ) : (
                  <>
                    <FaSyncAlt className="me-1" /> Générer
                  </>
                )}
              </Button>
              {supplierReport && (
                <>
                  <Button
                    color="secondary"
                    onClick={exportSupplierReportToPDF}
                  >
                    <FaDownload className="me-1" /> Télécharger
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
        <CardBody className="pt-2">
          <Row className="g-2 align-items-end mb-3 achats-filter-bar">
            <Col xl={3} lg={4} md={6} xs={12}>
              <FormGroup className="mb-0">
                <Label className="mb-1 fw-semibold">Article / code-barres</Label>
                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <FaSearch />
                  </span>
                  <Input
                    type="text"
                    placeholder="Nom ou code-barres..."
                    value={searchValue}
                    onChange={e => {
                      setSearchValue(e.target.value);
                    }}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        submitSearch(e.target.value);
                      }
                    }}
                  />
                  <Button color="light" className="border" onClick={() => submitSearch()}>
                    <FaBarcode />
                  </Button>
                </div>
              </FormGroup>
            </Col>
            <Col xl={3} lg={4} md={6} xs={12}>
              <FormGroup className="mb-0">
                <Label for="supplierSelect" className="form-label">
                  <FaUser className="me-1" /> Fournisseur
                </Label>
                <Select
                  id="supplierSelect"
                  options={fournisseurOptionsWithAll}
                  isLoading={suppliersLoading}
                  value={fournisseurOptionsWithAll.find(option => option.value === filters.supplierId)}
                  onChange={(selectedOption) => {
                    updateFilters({ supplierId: selectedOption ? selectedOption.value : null });
                  }}
                  placeholder="Sélectionner..."
                  isClearable
                  className="react-select-container"
                  classNamePrefix="react-select"
                  menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              </FormGroup>
            </Col>
            <Col xl={2} lg={4} md={6} xs={12}>
              <FormGroup className="mb-0">
                <Label for="monthFilter" className="form-label">
                  <FaCalendarAlt className="me-1" /> Mois
                </Label>
                <Input
                  type="month"
                  id="monthFilter"
                  value={filters.month}
                  onChange={e => handleMonthChange(e.target.value)}
                />
              </FormGroup>
            </Col>
            <Col xl={2} lg={4} md={6} xs={12}>
              <FormGroup className="mb-0">
                <Label for="startDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de début
                </Label>
                <Input
                  type="date"
                  id="dateDebut"
                  value={filters.dateDebut}
                  onChange={e => updateFilters({ month: '', dateDebut: e.target.value })}
                />
              </FormGroup>
            </Col>
            <Col xl={2} lg={4} md={6} xs={12}>
              <FormGroup className="mb-0">
                <Label for="endDate" className="form-label">
                  <FaCalendarAlt className="me-1" /> Date de fin
                </Label>
                <Input
                  type="date"
                  id="dateFin"
                  value={filters.dateFin}
                  onChange={e => updateFilters({ month: '', dateFin: e.target.value })}
                />
              </FormGroup>
            </Col>

          </Row>

          <div ref={supplierReportRef}>
            {supplierReport ? (
              <div className="client-report p-2 p-lg-3 bg-white animate__animated animate__fadeIn">
                <Row className="g-2 mb-3">
                  <Col lg={3} sm={6}>
                    <div className="achats-metric">
                      <span>Articles achetés</span>
                      <strong>{totalElements}</strong>
                    </div>
                  </Col>
                  <Col lg={3} sm={6}>
                    <div className="achats-metric">
                      <span>Quantite totale</span>
                      <strong>{Number(fullQuantityTotal || 0).toFixed(2)}</strong>
                    </div>
                  </Col>
                  <Col lg={3} sm={6}>
                    <div className="achats-metric">
                      <span>Total achats</span>
                      <strong>{formatPrice(Math.abs(fullAmountTotal || 0), false)}</strong>
                    </div>
                  </Col>
                  <Col lg={3} sm={6}>
                    <div className="achats-metric">
                      <span>Page affichee</span>
                      <strong>{totalPages ? `${currentPageNumber + 1}/${totalPages}` : '0/0'}</strong>
                    </div>
                  </Col>
                </Row>
                <div className="report-body mb-3">
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                    <div>
                      <h6 className="mb-1 fw-bold">Total acheté par article</h6>
                      <div className="text-muted small">
                        Affichage {pageStart} a {pageEnd} sur {totalElements} resultats.
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <Badge color="light" className="text-dark border">Totaux de toute la période</Badge>
                      <Input
                        type="select"
                        bsSize="sm"
                        value={pagination.pageSize}
                        onChange={handlePageSizeChange}
                        style={{ width: 96 }}
                      >
                        {PAGE_SIZE_OPTIONS.map(size => (
                          <option key={size} value={size}>{size}/page</option>
                        ))}
                      </Input>
                    </div>
                  </div>
                  <div className="table-responsive achats-table-wrap">
                   
                    <Table bordered hover size="sm" className="mb-0 align-middle">
                      <thead className="table-primary">
                        <tr>
                          <th className="text-center">Désignation</th>
                          <th className="text-center">Quantité achetée</th>
                          <th className="text-end">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bonsReception.map((note, index) => (
                          <tr key={note.id} className={index % 2 === 0 ? "bg-light" : ""}>
                            <td>
                              <div className="fw-semibold">{note.designation}</div>
                              <div className="text-muted small">
                                <FaBarcode className="me-1" />
                                {note.barcode || 'Sans code-barres'}
                              </div>
                            </td>
                            <td className="text-center">
                              <div className="fw-semibold">{note.quantity}</div>
                              <div className="text-muted small">{note.unit}</div>
                            </td>
                            <td className="text-end fw-bold">{formatPrice(note.lineTotal, false)}</td>
                          </tr>
                        ))}
                        {bonsReception.length === 0 && (
                          <tr>
                            <td colSpan="3" className="text-center text-muted py-4">
                              Aucun article acheté sur cette période.
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot className="table-light">
                        <tr>
                          <td className="text-end fw-bold">Total page:</td>
                          <td className="text-center fw-bold text-primary">
                            {Number(pageQuantityTotal || 0).toFixed(2)}
                          </td>
                          <td className="text-end fw-bold text-primary">{formatPrice(pageAmountTotal, false)}</td>
                        </tr>
                        <tr>
                          <td className="text-end fw-bold">Total resultats:</td>
                          <td className="text-center fw-bold text-success">{Number(fullQuantityTotal || 0).toFixed(2)}</td>
                          <td className="text-end fw-bold text-success">{formatPrice(Math.abs(fullAmountTotal || 0), false)}</td>
                        </tr>
                      </tfoot>
                    </Table>

                  </div>
                  <DocumentPagination
                    pageNumber={currentPageNumber}
                    totalPages={totalPages}
                    totalElements={totalElements}
                    pageSize={currentPageSize}
                    onPageChange={handlePageChange}
                  />
                </div>

                {supplierReport && supplierReport.summary && <div className="report-summary mb-4">
                  <Row className="g-4">
                    <Col md={6}>
                      <div className="mb-3 bg-light p-3 rounded-3 border border-1 border-secondary">
                        <h6 className="border-bottom pb-2 mb-3 text-secondary fw-bold">Résumé</h6>
                        <Table borderless size="sm" className="summary-table">
                          <tbody>
                            <tr>
                              <td className="fw-bold">Montant Total Consommé</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.totalConsome || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Solde Avant le {format(parseDate(supplierReport.period.from), 'dd/MM/yyyy', { locale: fr })}</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.soldeAvant || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Avance</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.avance || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Règlements normaux (hors avances)</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.reglements || 0, false)}</td>
                            </tr>
                            <tr>
                              <td className="fw-bold">Avoir</td>
                              <td className="text-end">{formatPrice(supplierReport.summary.avoir || 0, false)}</td>
                            </tr>
                            <tr className="table-light">
                              <td className="fw-bold">Net A Payer</td>
                              {
                                supplierReport.summary.netApaye < 0 ? (
                                  <td className="text-end fw-bold text-danger">{formatPrice(supplierReport.summary.netApaye, false)}</td>
                                ) : (
                                  <td className="text-end fw-bold text-success">{formatPrice(supplierReport.summary.netApaye, false)}</td>
                                )
                              }
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="amount-in-words p-4 bg-white rounded-3 border border-1 border-primary">
                        <p className="mb-1 fw-bold text-primary">Arrêté le présent document à la somme de:</p>
                        {
                          supplierReport.summary.netApaye < 0 ?
                            < p className="fst-italic text-danger fs-5">
                              {`Moins ${convertNumberToWords(Math.abs(supplierReport.summary.netApaye))} `}
                              Dirhams
                            </p> :
                            <p className="fst-italic text-success fs-5">
                              {`${convertNumberToWords(Math.abs(supplierReport.summary.netApaye))} `}
                              Dirhams
                            </p>
                        }
                        <p className="mt-3 small text-muted">
                          <FaCalendarAlt className="me-1" />
                          NB: Montant Total Consommé sur la période du <span className="fw-semibold">{format(parseDate(supplierReport.period.from), 'dd/MM/yyyy', { locale: fr })} au {format(parseDate(supplierReport.period.to), 'dd/MM/yyyy', { locale: fr })}</span>
                        </p>


                      </div>
                    </Col>
                  </Row>
                </div>
                }
                <div className="report-footer mt-4 pt-3 border-top text-muted small">
                  <div className="d-flex justify-content-between align-items-center">
                    <span><FaCalendarAlt className="me-1" /> Document généré le: <span className="fw-semibold">{supplierReport.generationDate}</span></span>
                    <span className="fw-bold">Page 1</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center p-5 bg-light rounded-4 border border-2 border-secondary animate__animated animate__fadeIn">
                {supplierReportLoading ? (
                  <div>
                    <Spinner color="primary" />
                    <p className="mt-3">Génération du rapport en cours...</p>
                  </div>
                ) : (
                  <div>
                    <FaFileAlt size={48} className="text-muted mb-3" />
                    <h5 className="fw-bold">Aucun rapport généré</h5>
                    <p className="text-muted">
                      Veuillez sélectionner un fournisseur et cliquer sur <span className="fw-semibold">"Générer le rapport"</span> pour afficher les détails des ventes.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      <style jsx>{`
        .achats-statistiques-page {
          background: #f6f8fb;
        }
        .achats-shell {
          border-radius: 8px;
        }
        .achats-title-icon {
          width: 36px;
          height: 36px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          background: #eef5ff;
          color: #0d6efd;
        }
        .achats-filter-bar {
          padding: 0.65rem 0.75rem;
          margin: 0 0 0.85rem 0;
          background: #f8fafc;
          border: 1px solid #e7edf5;
          border-radius: 8px;
        }
        .achats-metric {
          height: 100%;
          padding: 0.55rem 0.75rem;
          border: 1px solid #e8eef5;
          border-radius: 8px;
          background: #fbfdff;
        }
        .achats-metric span {
          display: block;
          color: #6c757d;
          font-size: 0.78rem;
          font-weight: 600;
          text-transform: uppercase;
        }
        .achats-metric strong {
          display: block;
          margin-top: 0.15rem;
          color: #1f2937;
          font-size: 1.05rem;
        }
        .achats-table-wrap {
          border: 1px solid #e6ebf1;
          border-radius: 8px;
          overflow: hidden;
        }
        .achats-table-wrap table thead th {
          background: #eef5ff;
          color: #1f3b57;
          border-bottom: 1px solid #dbe7f5;
          white-space: nowrap;
        }
        .achats-table-wrap table tbody td {
          vertical-align: middle;
        }
        .client-report {
          font-size: 0.9rem;
        }
        .summary-table td {
          padding: 0.4rem 0.75rem;
        }
        .company-info h5 {
          color: #2c3e50;
        }
        .amount-in-words {
          border-left: 3px solid #3498db;
        }
        @media print {
          .page-content {
            padding: 0 !important;
          }
          .client-report {
            border: none !important;
          }
        }
      `}</style>
    </Container>
  );
};

export default AchatsStatistiques;
